// Next turn (bottom right): a big ornate round button that advances the date one week.
// At the as-of date it reads "Present day": the ring keeps its gold, the core dims to a quiet
// teal and the ticks turn slowly, so it reads as live rather than broken. The date itself is
// shown in the top bar and timeline, not repeated here. Keyboard: Enter / Shift+Enter (Hud).

import { useSignal } from '@preact/signals';
import type { JSX } from 'preact';
import { useServices } from './context.ts';
import { fmtInt, turnOf } from './format.ts';
import { Icon } from './icons.tsx';

const RING_TICKS = 36;

export function NextTurn() {
  const { state, actions } = useServices();
  const busy = useSignal(false);
  const date = state.date.value;
  const present = date >= state.asOf.value;

  const advance = async () => {
    if (present || busy.value) {
      return;
    }
    busy.value = true;
    try {
      await actions.nextTurn();
    } finally {
      busy.value = false;
    }
  };

  const cls = ['wc-next', present ? 'wc-next--present' : '', busy.value ? 'wc-next--busy' : ''].join(' ');
  return (
    <div class={cls}>
      <div class="wc-next-banner">
        <span>{present ? 'Present day' : 'Next turn'}</span>
      </div>
      <button
        type="button"
        class="wc-next-btn wc-live"
        disabled={present}
        aria-label={present ? 'Present day: no later data' : 'Next turn: advance one week (Enter)'}
        onClick={advance}
      >
        <NextRing />
        <span class="wc-next-core">
          <Icon name={present ? 'globe' : 'next'} size={present ? 34 : 38} />
        </span>
      </button>
      <div class="wc-next-meta">
        <span class="wc-next-turn">
          Turn <b class="wc-num">{fmtInt(turnOf(date))}</b>
          {present ? null : <kbd>Enter</kbd>}
        </span>
      </div>
    </div>
  );
}

function NextRing() {
  const ticks: JSX.Element[] = [];
  for (let i = 0; i < RING_TICKS; i++) {
    const a = (i / RING_TICKS) * Math.PI * 2;
    const long = i % 3 === 0;
    const r0 = long ? 43 : 44.5;
    ticks.push(<line key={i} x1={50 + Math.cos(a) * r0} y1={50 + Math.sin(a) * r0} x2={50 + Math.cos(a) * 48} y2={50 + Math.sin(a) * 48} />);
  }

  return (
    <svg class="wc-next-ring" viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <linearGradient id="wc-next-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#fbe7b0" />
          <stop offset="0.4" stop-color="#c9a45c" />
          <stop offset="0.7" stop-color="#7d5d28" />
          <stop offset="1" stop-color="#e6c77a" />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="49" fill="#120d06" />
      <circle cx="50" cy="50" r="46" fill="none" stroke="url(#wc-next-gold)" stroke-width="5.5" />
      <g class="wc-next-ticks" stroke="#3b2a10" stroke-width="1">
        {ticks}
      </g>
      <circle cx="50" cy="50" r="40.5" fill="none" stroke="#1a1206" stroke-width="1.2" />
      <circle cx="50" cy="50" r="39" fill="none" stroke="url(#wc-next-gold)" stroke-width="1.4" opacity="0.8" />
      <path d="M50 1.5l3.5 5-3.5 5-3.5-5zM50 88.5l3.5 5-3.5 5-3.5-5zM1.5 50l5-3.5 5 3.5-5 3.5zM88.5 50l5-3.5 5 3.5-5 3.5z" fill="url(#wc-next-gold)" stroke="#1a1206" stroke-width="0.6" />
    </svg>
  );
}
